// vim: tabstop=8 softtabstop=0 noexpandtab shiftwidth=8 nosmarttab

import * as z from "zod";
import { throttle } from "throttle";
import {
	type AgentState,
	type AgentStateBase,
	type AgentStatusBase,
	GetAgentStateResponseSchema,
	type UpdateAgentStateRequest,
	type UpdateAgentStatusRequest,
} from "@dsbunny/rmm-schema";
import { BunnyBase } from "./bunny-base.js";
import { BunnyAgentImpl, BunnyAgentImplFactory } from "./bunny-agent-impl.js";
import { DesiredStateEventSource } from "./desired-state-event-source.js";
import { RMM_BASE_URL } from "./config.js";

export interface BunnyAgentSettings {
	agentId: string;
	implFactory: BunnyAgentImplFactory;
};

export class BunnyAgent extends BunnyBase {
	static URN = 'urn:dsbunny:agent:default';
	// Identity
	protected _agent_id: string;
	// State
	protected _cached_detail_as_text?: string | null;
	protected _polling: boolean = false;
	protected _has_error: boolean = false;
	protected _error_stack: any | null = null;
	// Implementation
	protected _impl?: BunnyAgentImpl;
	protected _implFactory: BunnyAgentImplFactory;

	get _storageKeyPrefix(): string {
		return `agent.${this.agentId}`;
	}

	constructor(options: BunnyAgentSettings) {
		super();
		this._agent_id = options.agentId;
		this._implFactory = options.implFactory;
		this._throttle_desired_state = throttle(async () => {
			try {
				if(this._polling) {
					return;
				}
				this._polling = true;
				await this._poll();
				if(this._has_error) {
					this._has_error = false;
					this._error_stack = null;
				}
			} catch(err: unknown) {
				console.warn(`poll: ${
					(err instanceof z.ZodError)
					? JSON.stringify({
						name: 'ZodError',
						detail: err.issues,
						stack: err.stack,
						cause: err.cause,
					})
					: (err instanceof Error)
					? JSON.stringify({
						name: err.name,
						detail: err.message,
						stack: err.stack,
						cause: err.cause,
					})
					: JSON.stringify({ err })
				}`);
				this._has_error = true;
				this._error_stack = (err instanceof Error) ? err.stack : null;
			} finally {
				this._polling = false;
			}
		}, 1000);
	}

	get agentId(): string {
		return this._agent_id;
	}

	get hasError(): boolean {
		return this._has_error;
	}

	get errorStack(): any | null {
		return this._error_stack || null;
	}

	// #region Polling
	poll(): void {
		if(typeof this._throttle_desired_state === 'undefined') {
			return;
		}
		this._throttle_desired_state();
	}

	async _poll(): Promise<void> {
		const agentId = this.agentId;
		if(typeof this._desired_state_event_source === 'undefined') {
			this._desired_state_event_source = await this._createDesiredStateEventSource(agentId);
		}
		// Pull desired state.
		await this._pollDesiredState(agentId);
		// Push runtime status.
		await this._reportRuntimeStatus(agentId);
	}

	async _pollDesiredState(agentId: string): Promise<void> {
		const {
			desiredState,
			etag,
			lastModified,
		} = await this._desiredState(
			agentId,
			this.desiredStateEtag,
			this.desiredStateLastModified,
		);
		if(desiredState === null) {
			console.log(`AGENT:${agentId}: No change in desired state.`);
			this._readDesiredState();
			return this._reportRuntimeState(agentId);
		}
		console.log(`AGENT:${agentId}: Changed detected.`);
		const hasChanged = await this._writeDesiredState(desiredState, etag, lastModified);
		if(hasChanged) {
			return this._reportRuntimeState(agentId);
		}
	};
	// #endregion

	// #region Notification
	async _createDesiredStateEventSource(agentId: string): Promise<DesiredStateEventSource> {
		if(!window.dsbunny.rmm.device) throw new Error('No device');
		const sse = await window.dsbunny.rmm.device.createAgentEventSource(agentId);
		sse.addEventListener('message', (_event) => {
			if(typeof this._throttle_desired_state === 'undefined') {
				return;
			}
			this._throttle_desired_state();
		});
		return sse;
	}
	// #endregion

	// #region Runtime State
	get _nullRuntimeState(): AgentStateBase {
		return {
			uri: null,
			pull_interval: null,
			push_interval: null,
			min_backoff_interval: null,
			max_backoff_interval: null,
			detail: null,
		};
	}

	async _reportRuntimeState(agentId: string): Promise<void> {
		if(!window.dsbunny.rmm.device) throw new Error('No device');
		try {
			const request: UpdateAgentStateRequest = await this._runtimeState();
			const url = `${RMM_BASE_URL}/api/v1/agents/${agentId}/runtime-state`;
			const response = await window.dsbunny.rmm.device.fetchProtectedResource(url, {
				method: 'PUT',
				headers: {
					'content-type': 'application/json',
				},
				body: JSON.stringify(request),
			});
			if(!response.ok) {
				throw new Error(`HTTP status: ${response.status}`);
			}
		} catch(err: unknown) {
			console.warn(`reportRuntimeState: ${
				(err instanceof Error) ? JSON.stringify({
					name: err.name,
					message: err.message,
					stack: err.stack,
					cause: err.cause,
				}) : JSON.stringify({ err })
			}`);
			throw err;
		}
	}

	async _runtimeState(): Promise<AgentStateBase> {
		if(!this.isActive
			|| typeof this._impl === 'undefined')
		{
			return this._nullRuntimeState;
		}
		const detail = await this._impl.runtimeStateDetail();
		return {
			uri: this.uri,
			pull_interval: this.pullInterval,
			push_interval: this.pushInterval,
			min_backoff_interval: this.minBackoffInterval,
			max_backoff_interval: this.maxBackoffInterval,
			detail,
		};
	}
	// #endregion

	// #region Runtime Status
	get _nullRuntimeStatus(): AgentStatusBase {
		return {
			uri: null,
			detail: null,
			has_error: false,
			error_stack: null,
		};
	}

	async _reportRuntimeStatus(agentId: string): Promise<void> {
		if(!window.dsbunny.rmm.device) throw new Error('No device');
		try {
			const request: UpdateAgentStatusRequest = await this._runtimeStatus();
			const url = `${RMM_BASE_URL}/api/v1/agents/${agentId}/runtime-status`;
			const response = await window.dsbunny.rmm.device.fetchProtectedResource(url, {
				method: 'PUT',
				headers: {
					'content-type': 'application/json',
				},
				body: JSON.stringify(request),
			});
			if(!response.ok) {
				throw new Error(`HTTP status: ${response.status}`);
			}
		} catch(err: unknown) {
			console.warn(`reportRuntimeStatus: ${
				(err instanceof Error) ? JSON.stringify({
					name: err.name,
					message: err.message,
					stack: err.stack,
					cause: err.cause,
				}) : JSON.stringify({ err })
			}`);
			throw err;
		}
	}

	async _runtimeStatus(): Promise<AgentStatusBase> {
		if(!this.isActive) {
			return this._nullRuntimeStatus;
		}
		if(typeof this._impl === 'undefined') {
			return {
				uri: this.uri,
				detail: null,
				has_error: this.hasError,
				error_stack: this.errorStack,
			};
		}
		return this._impl.runtimeStatus();
	}
	// #endregion

	// #region Desired State
	override _readDesiredState(): boolean {
		let hasChanged = super._readDesiredState();
		hasChanged = this._readDetail() || hasChanged;
		return hasChanged;
	}

	destroy(): void {
		this.shutdown();
		this._deleteDesiredState();
	}

	// Note that '|=' is not supported for `boolean` types.
	override async _writeDesiredState(
		desiredState: AgentState,
		etag: string | null,
		lastModified: string | null,
	): Promise<boolean> {
		let hasChanged = await super._writeDesiredState(desiredState, etag, lastModified);
		hasChanged = this._writeDetail(desiredState.detail) || hasChanged;
		return hasChanged;
	}

	override async _deleteDesiredState(): Promise<void> {
		await super._deleteDesiredState();
		this._writeDetail(null);
	}

	async _desiredState(
		agentId: string,
		etag: string | null,
		lastModified: string | null,
	): Promise<{
		desiredState: AgentState | null,
		etag: string | null,
		lastModified: string | null,
	}> {
		if(!window.dsbunny.rmm.device) throw new Error('No device');
		const headers: Record<string, string> = {};
		if(typeof etag === "string") {
			headers['if-none-match'] = etag;
		}
		if(typeof lastModified === "string") {
			headers['if-modified-since'] = lastModified;
		}
		const url = `${RMM_BASE_URL}/api/v1/agents/${agentId}/desired-state`;
		const response = await window.dsbunny.rmm.device.fetchProtectedResource(url, {
			method: 'GET',
			headers,
			cache: 'no-cache',
		});
		if(!response.ok) {
			if(response.status === 304) {
				return {
					desiredState: null,
					etag,
					lastModified,
				};
			}
			throw new Error(`HTTP status: ${response.status}`);
		}
		const json = await response.json();
		const result = GetAgentStateResponseSchema.parse(json);
		const desiredState = result satisfies AgentState;
		return {
			desiredState,
			etag: response.headers.get('etag'),
			lastModified: response.headers.get('last-modified'),
		};
	}

	override _onPullIntervalChanged(pullInterval: number | null): void {
		// no-op
	}
	override _onPushIntervalChanged(pushInterval: number | null): void {
		// no-op
	}
	override _onMinBackoffIntervalChanged(minBackoffInterval: number | null): void {
		// no-op
	}
	override _onMaxBackoffIntervalChanged(maxBackoffInterval: number | null): void {
		// no-op
	}
	// #endregion

	// #region URI
	// Apply the URI change.
	override async _onUriChanged(uri: string | null): Promise<void> {
		console.log(`${this.agentId}: onUri:`, uri);
		try {
			this._is_active = false;
			this._impl = await this._implFactory(this, uri);
			if(typeof this._impl === 'undefined') {
				throw new Error(`Implementation factory returned undefined for URI: ${uri}`);
			}
			this._is_active = (uri === this._impl.uri);

		} catch(err: unknown) {
			console.error(`${this.agentId}: _applyImpl:`, err);
			this._has_error = true;
			const error = err instanceof Error ? err : new Error('Unknown error');
			this._error_stack = error.stack;
		}
	}
	// #endregion

	// #region Detail
	get detail(): any {
		return (!!this._cached_detail_as_text)
			? JSON.parse(this._cached_detail_as_text)
			: null;
	}

	_readDetail(): boolean {
		const detailAsText = this._readKeyString('detail');
		if(detailAsText !== this._cached_detail_as_text) {
			this._cached_detail_as_text = detailAsText;
			this._onDetailChanged(JSON.parse(detailAsText || 'null'));
			return true;
		}
		return false;
	}

	// Returns true if the value has changed.
	_writeDetail(detail: any): boolean {
		const dataAsText = JSON.stringify(detail);
		if(dataAsText === this._cached_detail_as_text) {
			return false;
		}
		(async () => {
			this._onDetailChanged(detail || null);
			// Only write on success, enabling agent to read last detail.
			this._cached_detail_as_text = dataAsText;
			this._writeKeyString('detail', dataAsText);
		})().catch((err: unknown) => {
			console.error(`${this.agentId}: _onDetailChanged:`, err);
			this._has_error = true;
			const error = err instanceof Error ? err : new Error('Unknown error');
			this._error_stack = error.stack;
		});
		return true;
	}

	// Returns `true` if the value has changed.
	async _onDetailChanged(detail: any): Promise<boolean> {
		if(typeof this._impl === 'undefined') {
			this._has_error = false;
			this._error_stack = undefined;
			return false;
		}
		try {
			const hasChanged = await this._impl.onDesiredStateDetail(detail);
			this._has_error = false;
			this._error_stack = undefined;
			return hasChanged;
		} catch(err: unknown) {
			console.error(`${this.agentId}: onDetail:`, err);
			this._has_error = true;
			const error = err instanceof Error ? err : new Error('Unknown error');
			this._error_stack = error.stack;
			return false;  // Change not applied.
		}
	}
	// #endregion
};
